package com.milta.feature.route.data.remote.dto

import com.milta.feature.route.domain.entity.DeliveryDraft
import com.milta.feature.route.domain.entity.DeliveryGeocodeStatus
import com.milta.feature.route.domain.entity.OptimizationSkippedReason
import com.milta.feature.route.domain.entity.RouteCreationResult
import com.milta.feature.route.domain.entity.RouteCreationStatus
import com.milta.feature.route.domain.entity.toGeocodeStatus
import kotlinx.serialization.Serializable

/**
 * Mirrors the backend `CreateDeliveryDto` — one entry of the `deliveries` array (DOM-02).
 * [deadlineTime] ("HH:mm") is only ever populated for `POST /routes/{id}/deliveries` (contrat révisé
 * DOM-02, OCR scan confirmation) — `POST /routes` (initial route creation) never sets it, the
 * backend accepts `null` there the same way. [packageCount] mirrors the same optional, no-range-
 * validation field already accepted by `POST /routes`, `POST /routes/me` and
 * `POST /routes/{id}/deliveries` (MIL-35).
 */
@Serializable
data class CreateDeliveryDto(
    val recipientName: String,
    val address: String,
    val phoneNumber: String? = null,
    val deadlineTime: String? = null,
    val packageCount: Int? = null,
    val latitude: Double? = null,
    val longitude: Double? = null,
    /** Retour 29/10 — HERE's match returned with [latitude]/[longitude] at the draft stage, kept by the back with them. */
    val resolvedAddress: String? = null,
)

fun DeliveryDraft.toDto() = CreateDeliveryDto(
    recipientName = recipientName,
    address = address,
    phoneNumber = phoneNumber,
    deadlineTime = deadlineTime,
    packageCount = packageCount,
    latitude = latitude,
    longitude = longitude,
    resolvedAddress = resolvedAddress,
)

/** Request body for POST /routes (DOM-02, replaces the MIL-37 driver-only fallback body).
 * [date] mirrors [CreateOwnRouteRequest]'s own field — lets a manager assign a route for tomorrow
 * instead of always landing on server "today". Null falls back server-side. */
@Serializable
data class CreateRouteRequest(
    val driverAccountId: String,
    val deliveries: List<CreateDeliveryDto>,
    val date: String? = null,
    // MIL-99 — Enterprise's loading address, start point of the optimization (400
    // `loading_address_required` without it).
    val loadingAddressId: String? = null,
)

/** Mirrors the backend `DeliveryGeocodeStatusDto` — HERE validation outcome for one delivery,
 * returned right after creation/import. Never a replacement address, just a status. */
@Serializable
data class DeliveryGeocodeStatusResponseDto(
    val deliveryId: String,
    val geocodeStatus: String,
    // Retour 26/10 (demande 3) — the delivery's address, read for the zone name of a split; absent
    // from an older backend.
    val address: String? = null,
)

fun DeliveryGeocodeStatusResponseDto.toDomain() = DeliveryGeocodeStatus(
    deliveryId = deliveryId,
    geocodeStatus = geocodeStatus.toGeocodeStatus(),
    address = address,
)

/** Mirrors the backend RouteDetailDto (POST /routes response). */
@Serializable
data class CreateRouteResultDto(
    val id: String,
    val status: RouteCreationStatus,
    val stopsCount: Int,
    val driverAccountId: String,
    val createdAt: String,
    val deliveries: List<DeliveryGeocodeStatusResponseDto> = emptyList(),
    // MIL-97 — POST /routes/me now optimizes: the real outcome, and why it was skipped
    // ("QuotaExceeded" | "RoutarUnavailable"). Absent on the other create endpoints.
    val isOptimized: Boolean = false,
    val optimizationSkippedReason: String? = null,
    // Retour 26/10 (demande 4b) — RoutaR's km and duration of the optimized route; null when not
    // optimized, or from an older backend.
    val distanceKm: Double? = null,
    val durationMinutes: Int? = null,
)

fun CreateRouteResultDto.toDomain() = RouteCreationResult(
    id = id,
    status = status,
    stopsCount = stopsCount,
    driverAccountId = driverAccountId,
    createdAt = createdAt,
    deliveries = deliveries.map { it.toDomain() },
    isOptimized = isOptimized,
    optimizationSkippedReason = OptimizationSkippedReason.fromApi(optimizationSkippedReason),
    distanceKm = distanceKm,
    durationMinutes = durationMinutes,
)

/** Request body for POST /routes/me (MIL-33 "Créer / Optimiser ma tournée") — no driverAccountId,
 * the driver is always the caller. [date] ("yyyy-MM-dd", the driver's own local today/tomorrow) —
 * bug fix: lets a route be scheduled for tomorrow instead of always landing on server "today"
 * regardless of which Home tab (Today/Tomorrow) it was created from. Null falls back server-side. */
@Serializable
data class CreateOwnRouteRequest(
    val deliveries: List<CreateDeliveryDto>,
    val date: String? = null,
    // MIL-97 — the driver's GPS position, start point of the optimization; null when unavailable
    // or refused (never blocks the creation).
    val latitude: Double? = null,
    val longitude: Double? = null,
)
